import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { TodayWorkspaceItem } from '@norum/contracts';
import { appPath } from '@/api/client';
import { useTodayPost, TodayCallScope } from '@/api/TodayContext';
import { failureOf, said } from '@/api/errors';
import { addDays } from './grid';
import { localIsoDate } from './dates';

export function DayActions({ item, day, onUndo, scopeDepartment }: { item: TodayWorkspaceItem; day: Date; scopeDepartment: string; onUndo: (undo: () => Promise<void>) => void }) {
  return <TodayCallScope department={scopeDepartment}><Actions item={item} day={day} onUndo={onUndo} /></TodayCallScope>;
}
function Actions({ item, day, onUndo }: { item: TodayWorkspaceItem; day: Date; onUndo: (undo: () => Promise<void>) => void }) {
  const post = useTodayPost(), queries = useQueryClient();
  const [pending, setPending] = useState(false), [problem, setProblem] = useState<unknown>(null);
  async function act(op: 'pin' | 'postpone' | 'hide' | 'undo') {
    setPending(true); setProblem(null);
    try {
      await post(appPath(op === 'undo' ? '/v1/today/undo' : '/v1/today/place'), {
        itemKind: item.kind, sourceId: item.sourceId,
        ...(op === 'undo' ? {} : { op, plannedFor: localIsoDate(op === 'postpone' ? addDays(day, 1) : day) }),
      });
      if (op !== 'undo') onUndo(async () => {
        await post(appPath('/v1/today/undo'), { itemKind: item.kind, sourceId: item.sourceId });
        await queries.invalidateQueries({ queryKey: ['today-workspace'] });
      });
      await queries.invalidateQueries({ queryKey: ['today-workspace'] });
    } catch (error) { setProblem(error); }
    finally { setPending(false); }
  }
  return <div className="space-y-1">
    <div className="flex flex-wrap gap-3">
      <button className="underline min-h-11 min-w-11 px-2" disabled={pending} onClick={() => void act('pin')}>{item.pinned ? 'Unpin' : 'Pin'}</button>
      <button className="underline min-h-11 min-w-11 px-2" disabled={pending} onClick={() => void act('postpone')}>Tomorrow</button>
      <button className="underline min-h-11 min-w-11 px-2" disabled={pending} onClick={() => void act('hide')}>Hide for this day</button>
    </div>
    {problem !== null && <p role="alert">{said(failureOf(problem))}</p>}
  </div>;
}
